/**
 * Custom Popover
 *
 * Extended presets grid and calendar picker in a popover.
 */

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CalendarPicker } from "./calendar-picker";
import {
  EXTENDED_PRESETS,
  type ExtendedPreset,
  type DateRangeValue,
  type CustomDateRange,
  isCustomRange,
} from "./types";
import { formatDateForInput, getDateRangeFromPreset } from "./utils";
import type { DateRange } from "react-day-picker";

type CustomPopoverContentProps = {
  /** Current selected value */
  value: DateRangeValue;
  /** Callback when an extended preset is selected */
  onExtendedPresetSelect: (preset: ExtendedPreset) => void;
  /** Callback when a custom range is applied */
  onCustomRangeApply: (range: CustomDateRange) => void;
  /** Callback to close the popover */
  onClose: () => void;
};

export function CustomPopoverContent({
  value,
  onExtendedPresetSelect,
  onCustomRangeApply,
  onClose,
}: CustomPopoverContentProps) {
  // Initialize calendar with current value if it's a custom range
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(() => {
    if (isCustomRange(value)) {
      return { from: value.from, to: value.to };
    }
    // If it's an extended preset, show its range in the calendar
    if (typeof value === "string" && EXTENDED_PRESETS.some(p => p.value === value as string)) {
      const range = getDateRangeFromPreset(value);
      return { from: range.from, to: range.to };
    }
    return undefined;
  });

  // Track if user has modified the calendar selection
  const [, setHasCalendarSelection] = useState(
    isCustomRange(value)
  );

  // Reset state when popover opens with new value
  useEffect(() => {
    if (isCustomRange(value)) {
      setSelectedRange({ from: value.from, to: value.to });
      setHasCalendarSelection(true);
    } else if (typeof value === "string" && EXTENDED_PRESETS.some(p => p.value === value)) {
      const range = getDateRangeFromPreset(value as ExtendedPreset);
      setSelectedRange({ from: range.from, to: range.to });
      setHasCalendarSelection(false);
    } else {
      setSelectedRange(undefined);
      setHasCalendarSelection(false);
    }
  }, [value]);

  const handlePresetClick = (preset: ExtendedPreset) => {
    onExtendedPresetSelect(preset);
    onClose();
  };

  const handleCalendarSelect = (range: DateRange | undefined) => {
    setSelectedRange(range);
    setHasCalendarSelection(true);
  };

  const handleApply = () => {
    if (selectedRange?.from && selectedRange?.to) {
      onCustomRangeApply({
        type: "custom",
        from: selectedRange.from,
        to: selectedRange.to,
      });
      onClose();
    }
  };

  const isExtendedPreset = typeof value === "string" && 
    EXTENDED_PRESETS.some(p => p.value === value);

  return (
    <div className="w-[320px] p-3 space-y-4">
      {/* Extended Presets Grid */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Quick Ranges
        </h4>
        <div className="grid grid-cols-2 gap-1.5">
          {EXTENDED_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => handlePresetClick(preset.value)}
              className={cn(
                "px-3 py-2 text-xs font-medium rounded-md transition-all text-left",
                value === preset.value && isExtendedPreset
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/50 text-foreground hover:bg-muted"
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border" />

      {/* Calendar Section - Simplified */}
      <div className="space-y-3">
        {/* Calendar */}
        <CalendarPicker
          selected={selectedRange}
          onSelect={handleCalendarSelect}
          className="mx-auto"
        />

        {/* Selected range display - subtle, below calendar */}
        {(selectedRange?.from || selectedRange?.to) && (
          <div className="text-center text-xs text-muted-foreground">
            {selectedRange?.from && selectedRange?.to ? (
              <span>
                {formatDateForInput(selectedRange.from)} — {formatDateForInput(selectedRange.to)}
              </span>
            ) : selectedRange?.from ? (
              <span>Select end date</span>
            ) : null}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-xs h-8"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleApply}
            disabled={!selectedRange?.from || !selectedRange?.to}
            className="text-xs h-8"
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}

